/**
 * Reading a buyer's search history: how old a row is, and which rows are the
 * same search.
 *
 * Every search writes its own document, so a query run five times is five
 * documents. Listing them raw put three copies of "6020-T8" in "Recent
 * searches" at once, and deleting one left its twins behind — the row seemed to
 * vanish and come straight back. One row per search fixes that, provided
 * deleting the row deletes every document behind it.
 */
import { SearchHistoryItem } from "../buyerStateDefs";

/**
 * A history timestamp as a Date, whatever shape it was stored in. Until August
 * 2026 the app echoed each item back to the server after a search, and a
 * Timestamp serialised to JSON — {type: "firestore/timestamp/1.0", seconds,
 * nanoseconds} — was written over the real one, so older rows carry a plain map
 * with no toDate() on it.
 */
export const historyTimestampToDate = (value: unknown): Date | null => {
  if (!value) return null;
  const valid = (date: Date) => (Number.isNaN(date.getTime()) ? null : date);
  if (value instanceof Date) return valid(value);
  const candidate = value as {
    toDate?: () => Date;
    seconds?: number;
    _seconds?: number;
  };
  if (typeof candidate.toDate === "function") {
    try {
      return valid(candidate.toDate());
    } catch {
      return null;
    }
  }
  const seconds = candidate.seconds ?? candidate._seconds;
  if (typeof seconds === "number") return valid(new Date(seconds * 1000));
  if (typeof value === "string" || typeof value === "number") {
    return valid(new Date(value));
  }
  return null;
};

/** One search, and every document that recorded it. */
export type CollapsedSearch = {
  /** Groups documents; also a stable React key. */
  key: string;
  /** The newest document for this search: what the row shows and links to. */
  item: SearchHistoryItem;
  at: Date;
  /** Ids of every document behind the row, newest first. */
  ids: string[];
  /** How many times the buyer ran it (within the day, when grouped by day). */
  count: number;
  /** True when any of them carried filters beyond the query text. */
  hasFilters: boolean;
};

/** Filters narrow a search beyond its words, so a row can say so. */
export const hasExtraFilters = (item: SearchHistoryItem): boolean => {
  if (!item.urlQueryString) return false;
  const parameters = new URLSearchParams(item.urlQueryString);
  return (
    parameters.get("shape") !== null || parameters.get("material") !== null
  );
};

/** The words the buyer searched for, as typed. */
export const searchText = (item: SearchHistoryItem): string => {
  if (item.query) return item.query;
  return new URLSearchParams(item.urlQueryString || "").get("q") || "";
};

/**
 * One entry per search, newest first. Two documents are the same search when
 * they carry the same words, ignoring case — which is what the buyer sees, so
 * it is what a row has to mean. `perDay` keeps a search that was run on several
 * days as one row per day, for a history laid out by date.
 */
export const collapseSearchHistory = (
  items: SearchHistoryItem[],
  options?: { perDay?: boolean },
): CollapsedSearch[] => {
  const groups = new Map<string, CollapsedSearch>();
  for (const item of items) {
    const at = historyTimestampToDate(item.timestamp);
    const text = searchText(item).trim();
    if (!at || !text) continue;
    const key = options?.perDay
      ? `${at.toDateString()}|${text.toLowerCase()}`
      : text.toLowerCase();
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        key,
        item,
        at,
        ids: item.id ? [item.id] : [],
        count: 1,
        hasFilters: hasExtraFilters(item),
      });
      continue;
    }
    existing.count += 1;
    existing.hasFilters = existing.hasFilters || hasExtraFilters(item);
    if (item.id) existing.ids.push(item.id);
    if (at.getTime() > existing.at.getTime()) {
      // The newest wins the row, so its filters are the ones it links to.
      existing.item = item;
      existing.at = at;
      existing.ids = [
        item.id || "",
        ...existing.ids.filter((id) => id !== item.id),
      ].filter(Boolean);
    }
  }
  const searches: CollapsedSearch[] = [];
  groups.forEach((group) => searches.push(group));
  return searches.sort((a, b) => b.at.getTime() - a.at.getTime());
};

/** Where clicking a row goes: the newest form of that search. */
export const searchTargetUrl = (item: SearchHistoryItem): string =>
  item.urlQueryString
    ? `/${item.urlQueryString}`
    : `/?q=${encodeURIComponent(item.query || "")}`;
